import { baseRestV4Api as api } from "../base/base-rest-v4-api";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTheme: build.query<GetThemeApiResponse, GetThemeApiArg>({
      query: () => ({ url: `/v4/theme` }),
    }),
    createdDynamicView: build.mutation<
      CreatedDynamicViewApiResponse,
      CreatedDynamicViewApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/schedules/reports/view`,
        method: "POST",
        body: queryArg.scheduleReportFiltersDto,
      }),
    }),
    getAllSimpleModules: build.query<
      GetAllSimpleModulesApiResponse,
      GetAllSimpleModulesApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/modules/simple`,
        params: {
          includeFormList: queryArg.includeFormList,
          filter: queryArg.filter,
        },
      }),
    }),
    getSimpleHierarchyTypes1: build.query<
      GetSimpleHierarchyTypes1ApiResponse,
      GetSimpleHierarchyTypes1ApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/hierarchy-types/simple`,
        params: {
          type: queryArg["type"],
          withValue: queryArg.withValue,
          filter: queryArg.filter,
        },
      }),
    }),
    getEnvironment: build.query<
      GetEnvironmentApiResponse,
      GetEnvironmentApiArg
    >({
      query: () => ({ url: `/v4/schemas/current` }),
    }),
    getSchedulesReportsView: build.query<
      GetSchedulesReportsViewApiResponse,
      GetSchedulesReportsViewApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/schedules/reports/view/${queryArg.viewId}`,
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          sort: queryArg.sort,
          order_by: queryArg.orderBy,
          includeDetails: queryArg.includeDetails,
          includeTemplates: queryArg.includeTemplates,
        },
      }),
    }),
    detailedCountForView: build.query<
      DetailedCountForViewApiResponse,
      DetailedCountForViewApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/schedules/reports/view/${queryArg.viewId}/detailed-count`,
      }),
    }),
    countProjectedRecordsForView: build.query<
      CountProjectedRecordsForViewApiResponse,
      CountProjectedRecordsForViewApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/schedules/reports/view/${queryArg.viewId}/count-projected-records`,
      }),
    }),
    getSimpleHierarchyValues: build.query<
      GetSimpleHierarchyValuesApiResponse,
      GetSimpleHierarchyValuesApiArg
    >({
      query: (queryArg) => ({
        url: `/v4/hierarchy-values/simple`,
        params: {
          filter: queryArg.filter,
          sort: queryArg.sort,
          order_by: queryArg.orderBy,
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          ignoreUserHierarchyRestrictions:
            queryArg.ignoreUserHierarchyRestrictions,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as restV4Api };
export type GetThemeApiResponse = /** status 200 OK */ {
  [key: string]: string;
};
export type GetThemeApiArg = void;
export type CreatedDynamicViewApiResponse =
  /** status 200 OK */ SchedulesDynamicViewDto;
export type CreatedDynamicViewApiArg = {
  scheduleReportFiltersDto: ScheduleReportFiltersDto;
};
export type GetAllSimpleModulesApiResponse =
  /** status 200 OK */ EntityPageResponseDtoModuleDtoObject;
export type GetAllSimpleModulesApiArg = {
  includeFormList?: boolean;
  /** Accepts a list of filters.
    The format is `?filter=field:operator:value`, e.g. `deleted:eq:true` to show deleted records.
    
    The valid operators are: 'eq', 'neq'.
    - Supported filters: `enableCalendarView`
     */
  filter?: string[];
};
export type GetSimpleHierarchyTypes1ApiResponse =
  /** status 200 OK */ EntityResponseDtoListHierarchyTypeDtoObject;
export type GetSimpleHierarchyTypes1ApiArg = {
  type?: number[];
  withValue?: boolean;
  /** Accepts a list of filters.
    The format is `?filter=field:operator:value`, e.g. `archived:eq:false` to return hierarchy types that are not archived.
    
    Accepted operators are 'eq', 'neq', 'gt', 'lt', 'like', 'eq_case_insensitive', 'start_with', and 'in'.
    The value can be any value appearing on the JSON response.
    
    Available filter: id, archived, caption. */
  filter?: string[];
};
export type GetEnvironmentApiResponse = /** status 200 OK */ {
  [key: string]: string;
};
export type GetEnvironmentApiArg = void;
export type GetSchedulesReportsViewApiResponse =
  /** status 200 OK */ EntityPageResponseDtoi18NReport;
export type GetSchedulesReportsViewApiArg = {
  page?: number;
  pageSize?: number;
  viewId: string;
  /** Accepts a list of field names to be used for sorting.
    <p>Supported fields: module_form, scheduled_description, title. The field module_form will sort by form caption</p>  */
  sort?: string[];
  /** The sort order of the properties defined in sort parameter. */
  orderBy?: "DESC" | "ASC";
  /** When this is set to true the response contains the status and hierarchy fields of projected records */
  includeDetails?: boolean;
  /** When this is set to true the response contains the template for each schedule. */
  includeTemplates?: boolean;
};
export type DetailedCountForViewApiResponse =
  /** status 200 OK */ EntityResponseDtoMapStringListMapStringLong;
export type DetailedCountForViewApiArg = {
  viewId: string;
};
export type CountProjectedRecordsForViewApiResponse = /** status 200 OK */ {
  [key: string]: object;
};
export type CountProjectedRecordsForViewApiArg = {
  viewId: string;
};
export type GetSimpleHierarchyValuesApiResponse =
  /** status 200 OK */ EntityPageResponseDtoHierarchyFieldDtoObject;
export type GetSimpleHierarchyValuesApiArg = {
  /** Accepts a list of filters.
    The format is `?filter=field:operator:value`, e.g. `typeId:eq:1` to return hierarchy fields with the given hierarchyTypeId.
    
    Accepted operators are 'eq', 'neq', 'gt', 'lt', 'like', 'eq_case_insensitive', 'start_with', and 'in'.
    
    Available filters: id, typeId, archived, caption, parentId */
  filter?: string[];
  /** Comma separated list of the field names to sort by.
    The order of the fields is significant. By default the order is ascending, and case insensitive for string properties.
    Available fields:  id, typeId, archived, caption, deleted */
  sort?: string[];
  orderBy?: "DESC" | "ASC";
  page?: number;
  pageSize?: number;
  ignoreUserHierarchyRestrictions?: boolean;
};
export type SchedulesDynamicViewDto = {
  hashKey?: string;
};
export type ScheduleReportFiltersDto = {
  hierarchyFieldIds?: number[];
  moduleFormIds?: number[];
  startDate?: string;
  endDate?: string;
};
export type ResponseMessageDto = {
  index?: number;
  code?: string;
  key?: string;
  message?: string;
};
export type Validation = {
  /** the errors that occured while attempting the requested operation */
  errors?: string[];
  /** the warnings that occured while attempting the requested operation */
  warnings?: string[];
  /** the new format errors that occured while attempting the requested operation */
  responseErrors?: ResponseMessageDto[];
  /** the new format warnings that occured while attempting the requested operation */
  responseWarnings?: ResponseMessageDto[];
};
export type GroupEditAccessDto = {
  allGroups?: boolean;
  groups?: number[];
};
export type WorkflowAccessDto = {
  allModules?: boolean;
  modules?: number[];
};
export type AdminAccessDto = {
  adminAccess?:
    | "BASIC_FORMBUILDER_READ"
    | "ADVANCED_FORMBUILDER_READ"
    | "NOTIFICATIONS_READ"
    | "HIERARCHIES_READ"
    | "ROLES_READ"
    | "USERS_READ"
    | "RISK_MATRIX_READ"
    | "MODULES_READ"
    | "SCHEDULES_READ"
    | "OCCUPATION_READ"
    | "TRAINING_MANAGEMENT_READ"
    | "LICENSING_READ"
    | "INDIVIDUAL_DASHBOARD_TAB_READ"
    | "BRANDING_READ"
    | "BASIC_FORMBUILDER_CREATE"
    | "ADVANCED_FORMBUILDER_CREATE"
    | "NOTIFICATIONS_CREATE"
    | "HIERARCHIES_CREATE"
    | "ROLES_CREATE"
    | "USERS_CREATE"
    | "MODULES_CREATE"
    | "SCHEDULES_CREATE"
    | "OCCUPATION_CREATE"
    | "TRAINING_MANAGEMENT_CREATE"
    | "INDIVIDUAL_DASHBOARD_TAB_CREATE"
    | "BASIC_FORMBUILDER_MODIFY"
    | "ADVANCED_FORMBUILDER_MODIFY"
    | "NOTIFICATIONS_MODIFY"
    | "HIERARCHIES_MODIFY"
    | "ROLES_MODIFY"
    | "USERS_MODIFY"
    | "RISK_MATRIX_MODIFY"
    | "MODULES_MODIFY"
    | "SCHEDULES_MODIFY"
    | "OCCUPATION_MODIFY"
    | "TRAINING_MANAGEMENT_MODIFY"
    | "INDIVIDUAL_DASHBOARD_TAB_MODIFY"
    | "BRANDING_MODIFY"
    | "DASHBOARD_ADMINISTRATE_LAYOUT"
    | "ADVANCED_FORMBUILDER_DELETE"
    | "TRAINING_MANAGEMENT_DELETE"
    | "LOGS_READ"
    | "MANAGE_ACCOUNT"
    | "BASIC_FORMBUILDER_DELETE_FIELDS"
    | "INDIVIDUAL_DASHBOARD_TAB_DELETE"
    | "HOME_PAGE_READ"
    | "HOME_PAGE_MODIFY"
    | "MODULES_BULK_EDIT"
    | "ROLES_ADMIN_ACCESS"
    | "NAME_UPDATES"
    | "AUTO_RULE"
    | "TRAINING_MATRIX_READ"
    | "GAP_ANALYSIS_READ"
    | "ONLINE_LEARNING_FULL_ADMIN_ACCESS"
    | "ONLINE_LEARNING_ENROLMENT_ACCESS"
    | "USER_STATUSES_MODIFY"
    | "MOBILE_SETTINGS_READ"
    | "MOBILE_SETTINGS_MODIFY"
    | "AI_READ"
    | "AI_MODIFY"
    | "TRANSLATION_AUTOTRANSLATE";
  groupEditAccess?: GroupEditAccessDto;
  workflowAccess?: WorkflowAccessDto;
};
export type RoleDtoObject = {
  id?: number;
  caption?: object;
  managerEmail?: string;
  managerLocaleId?: number;
  archived?: boolean;
  externalRoleName?: string;
  version?: number;
  adminAccess?: AdminAccessDto[];
};
export type AllFormConfigurationPermissionDto = {
  groupsAbleToManageGlobalViews?: number[];
};
export type WorkflowStepAccessDto = {
  permissionType?:
    | "READ_ROLE"
    | "EDIT_ROLE"
    | "PROGRESS_ROLE"
    | "INVITATION_READ_ROLE"
    | "INVITATION_EDIT_ROLE"
    | "ATTACHMENT_READ_ROLE"
    | "ATTACHMENT_EDIT_ROLE"
    | "VERSION_READ_ROLE"
    | "VERSION_EDIT_ROLE"
    | "RECORD_READ_ROLE"
    | "HIERARCHY_READ_ROLE"
    | "HIERARCHY_EDIT_ROLE"
    | "PERSONFIELD_READ_ROLE"
    | "PERSONFIELD_EDIT_ROLE"
    | "PERSONFIELD_INVITATION_READ_ROLE"
    | "PERSONFIELD_INVITATION_EDIT_ROLE"
    | "PERSONFIELD_ATTACHMENT_READ_ROLE"
    | "PERSONFIELD_ATTACHMENT_EDIT_ROLE"
    | "PERSONFIELD_VERSION_READ_ROLE"
    | "PERSONFIELD_VERSION_EDIT_ROLE"
    | "PERSONFIELD_HIERARCHY_READ_ROLE"
    | "PERSONFIELD_HIERARCHY_EDIT_ROLE"
    | "ACKNOWLEDGEMENT_READ_ROLE"
    | "ACKNOWLEDGEMENT_EDIT_ROLE"
    | "PERSONFIELD_ACKNOWLEDGEMENT_READ"
    | "PERSONFIELD_ACKNOWLEDGEMENT_EDIT";
  roleIds?: number[];
  personFieldIds?: number[];
  fieldGroupId?: number;
};
export type FieldVisibilityDto = {
  id?: number;
  parentId?: number;
  fieldVisibilityType?:
    | "FIELD"
    | "QUESTIONNAIRE"
    | "RISKRATING"
    | "HIERARCHY"
    | "GROUP"
    | "RECORDLINK";
  sourceFieldId?: number;
  sourceFieldName?: string;
  sourceSubField?: string;
  targetValueId?: object;
  targetValue?: object;
  targetValueType?:
    | "STRING"
    | "DATE"
    | "BOOLEAN"
    | "NUMBER"
    | "ENTITY_ID"
    | "TRAINING";
  filterOperator?:
    | "eq"
    | "neq"
    | "gt"
    | "lt"
    | "gte"
    | "lte"
    | "like"
    | ""
    | "eq_case_insensitive"
    | "eq_case_sensitive"
    | "start_with"
    | "in"
    | "id_eq"
    | "id_neq"
    | "id_in"
    | "is_blank"
    | "in_with_blank"
    | "id_in_with_blank"
    | "is_archived"
    | "between";
  conjugate?: "AND" | "OR";
  conditionOperator?:
    | "CONTAINS"
    | "DOES_NOT_CONTAIN"
    | "IS_BLANK"
    | "IS_NOT_BLANK"
    | "IS"
    | "IS_NOT"
    | "CHECKED"
    | "UNCHECKED"
    | "IS_FOR_ONLINE_LEARNING"
    | "IS_A_MEMBER_OF"
    | "IS_NOT_A_MEMBER_OF"
    | "HAS_ALL"
    | "HAS_ANY"
    | "HAS_NONE";
  negation?: boolean;
  order?: number;
  checkNull?: boolean;
};
export type WorkflowStepDtoObject = {
  id?: number;
  workflowId?: number;
  label?: object;
  caption?: object;
  shownInRecordList?: boolean;
  shownInRecordListOrder?: number;
  availableInRecordList?: boolean;
  archived?: boolean;
  archivedStep?: boolean;
  draftMarker?: boolean;
  draft?: boolean;
  hideRecordSaveButton?: boolean;
  editModeByDefault?: boolean;
  rolesPermissions?: WorkflowStepAccessDto[];
  backgroundColor?: string;
  nextSteps?: WorkflowStepDtoObject[];
  buttonCaption?: object;
  nextStepVisibilities?: FieldVisibilityDto[];
  nextStepId?: number;
  promptBeforeAdvance?: boolean;
  closeOnSave?: boolean;
  submissionBehaviour?:
    | "KEEP_RECORD_OPEN"
    | "CLOSE_RECORD"
    | "CLOSE_CURRENT_TAB_OR_WINDOW"
    | "NAVIGATE_TO_URL";
  navigationUrl?: string;
  skipMandatoryFieldValidation?: boolean;
  hintCaption?: object;
  icon?: string;
  progressPersonFieldIds?: number[];
};
export type ProgressConditionDto = {
  id?: number;
  archived?: boolean;
  interval?: string;
  repeat?: boolean;
  sourceStepId?: number;
  targetStepId?: number;
  moduleFormId?: number;
};
export type TranslationDto = {
  id?: number;
  localeId?: number;
  value?: string;
};
export type TranslationUnitDto = {
  id?: number;
  description?: string;
  translations?: TranslationDto[];
};
export type ProgressConditionSummaryDtoTranslationUnitDtoBase = {
  id?: number;
  archived?: boolean;
  fieldName?: TranslationUnitDto;
  sourceWorkflowStepName?: TranslationUnitDto;
  targetWorkflowStepName?: TranslationUnitDto;
  conjugate?: "AND" | "OR";
  type: string;
};
export type ParsedInterval = {
  value?: number;
  unit?: "DAYS" | "WEEKS" | "MONTHS" | "YEARS";
};
export type DateProgressConditionSummaryDto = {
  type: "DateProgressConditionSummaryDTO";
} & ProgressConditionSummaryDtoTranslationUnitDtoBase & {
    fieldName?: object;
    sourceWorkflowStepName?: object;
    targetWorkflowStepName?: object;
    repeat?: boolean;
    interval?:
      | "ONE_DAY"
      | "TWO_DAYS"
      | "THREE_DAYS"
      | "FOUR_DAYS"
      | "FIVE_DAYS"
      | "SIX_DAYS"
      | "ONE_WEEK"
      | "TWO_WEEKS"
      | "THREE_WEEKS"
      | "ONE_MONTH"
      | "TWO_MONTHS"
      | "THREE_MONTHS"
      | "SIX_MONTHS"
      | "ONE_YEAR";
    splitInterval?: ParsedInterval;
  };
export type IntegerProgressConditionSummaryDto = {
  type: "IntegerProgressConditionSummaryDTO";
} & ProgressConditionSummaryDtoTranslationUnitDtoBase & {
    fieldName?: object;
    sourceWorkflowStepName?: object;
    targetWorkflowStepName?: object;
    firstOccurrence?: number;
    cycleInterval?: number;
    cycleIntervalEnabled?: boolean;
  };
export type PersonProgressConditionSummaryDto = {
  type: "PersonProgressConditionSummaryDTO";
} & ProgressConditionSummaryDtoTranslationUnitDtoBase & {
    fieldName?: object;
    sourceWorkflowStepName?: object;
    targetWorkflowStepName?: object;
    personFieldAction?: "ARCHIVED" | "ACTIVE";
  };
export type RecordLinkProgressConditionSummaryDto = {
  type: "RecordLinkProgressConditionSummaryDTO";
} & ProgressConditionSummaryDtoTranslationUnitDtoBase & {
    fieldName?: object;
    sourceWorkflowStepName?: object;
    targetWorkflowStepName?: object;
    linkedRecordsTargetWorkflowStepCaption?: object;
    scope?: "ALL" | "ANY" | "NO";
    reverseRecordLink?: boolean;
  };
export type ModuleFormDtoObject = {
  id?: number;
  caption?: object;
  removed?: boolean;
  workflowId?: number;
  roles?: RoleDtoObject[];
  workflowSteps?: WorkflowStepDtoObject[];
  progressConditions?: ProgressConditionDto[];
  progressConditionSummaries?: (
    | DateProgressConditionSummaryDto
    | IntegerProgressConditionSummaryDto
    | PersonProgressConditionSummaryDto
    | RecordLinkProgressConditionSummaryDto
  )[];
  displayInForms?: boolean;
  sourceId?: string;
  dateRange?:
    | "THIS_MONTH"
    | "LAST_THREE_MONTHS"
    | "LAST_SIX_MONTHS"
    | "THIS_YEAR"
    | "ALL";
  defaultForm?: boolean;
  lastUsed?: string;
  enableCalendarView?: boolean;
  /** Whether this form is visible in the mobile app */
  availableInMobileApp?: boolean;
  /** Whether this form is enabled for offline use in the mobile app (records and supporting data are synced to the device). When false, the form may still be visible online-only. */
  enabledForOffline?: boolean;
  /** Whether to sync all records for this form (overrides mobileInitialRecordNumber) */
  syncAllRecords?: boolean;
  /** Whether to download file attachments during mobile sync for offline access */
  syncAttachments?: boolean;
  /** Whether to download archived records during mobile sync for offline access */
  syncArchivedRecords?: boolean;
  /** Whether to download embedded images (in rich-text and history fields) during mobile sync for offline access */
  syncEmbeddedImages?: boolean;
  /** Whether the mobile record list shows the per-row attachment affordance for this form. Null is treated as true (the default) for backward compatibility. */
  showRecordListAttachments?: boolean;
  /** Maximum number of records to sync per form when syncAllRecords is false */
  mobileInitialRecordNumber?: number;
  /** When to sync form data: ONLOGIN (eager) or ONRECORDLISTLOAD (deferred until user navigates) */
  mobileInitialFormType?: "ONLOGIN" | "ONRECORDLISTLOAD";
};
export type ModuleDtoObject = {
  id?: number;
  caption?: object;
  viewId?: string;
  type?: "MODULE" | "EMBEDDED_URL" | "DASHBOARD";
  icon?: string;
  sourceUrl?: string;
  displayInMobile?: boolean;
  target?: "CURRENT_WINDOW" | "NEW_WINDOW";
  roles?: RoleDtoObject[];
  allFormConfigurationPermissionDTO?: AllFormConfigurationPermissionDto;
  removed?: boolean;
  hidden?: boolean;
  position?: number;
  moduleGroupId?: number;
  includeUserName?: boolean;
  accountablePersonEnabled?: boolean;
  showAllFormsFirst?: boolean;
  disableAllForms?: boolean;
  forms?: ModuleFormDtoObject[];
};
export type EntityPageResponseDtoModuleDtoObject = {
  validation?: Validation;
  /** the number of page */
  page?: number;
  /** the total number of pages associated with the original request */
  availablePages?: number;
  size?: number;
  totalSize?: number;
  items?: ModuleDtoObject[];
};
export type HierarchyFieldDtoObject = {
  id?: number;
  hierarchyTypeId?: number;
  hierarchyTypeCaption?: object;
  parentId?: number;
  caption?: object;
  longitude?: number;
  latitude?: number;
  geofenceEnabled?: boolean;
  geofenceRadius?: number;
  archived?: boolean;
  lastChange?: string;
  deleted?: boolean;
  hierarchyFieldParentCaption?: object;
};
export type HierarchyTypeConfigDto = {
  id?: number;
  useInitialValue?: boolean;
  showAll?: boolean;
  editable?: boolean;
  upperBoundHierarchyTypeId?: number;
};
export type HierarchyTypeDtoObject = {
  id?: number;
  parentId?: number;
  caption?: object;
  weight?: number;
  version?: number;
  archived?: boolean;
  external?: boolean;
  mandatoryForUserRecords?: boolean;
  mandatoryForNewForms?: boolean;
  mandatoryTypeConfig?: boolean;
  shownInRecordListOrder?: number;
  shownInRecordList?: boolean;
  availableInRecordList?: boolean;
  defaultHierarchyValueParentId?: number;
  values?: HierarchyFieldDtoObject[];
  hierarchyTypeConfigDTO?: HierarchyTypeConfigDto;
  dependsOnParent?: boolean;
};
export type EntityResponseDtoListHierarchyTypeDtoObject = {
  validation?: Validation;
  result?: HierarchyTypeDtoObject[];
};
export type UserConfigDto = {
  authorConfigType?: "SCHEDULER_AUTHOR" | "NON_SCHEDULER_AUTHOR";
};
export type UserDto = {
  id?: number;
  value?: string;
  managerId?: number;
  managerValue?: string;
  managerArchived?: boolean;
  archived?: boolean;
  userConfigDTO?: UserConfigDto;
  displayName?: string;
};
export type AttachmentDto = {
  id?: number;
  name?: string;
  person?: UserDto;
  recordId?: number;
  lastModified?: string;
  sourceId?: string;
  deleted?: boolean;
  fileKey?: string;
  url?: string;
};
export type ConfirmationItemDto = {
  objectKey?: string;
  presignedUrl?: string;
  fileName?: string;
  originalRecordStatus?: string;
};
export type QuestionnaireAttachmentConfirmationDto = {
  objectKey?: string;
  presignedUrl?: string;
  fileName?: string;
  originalRecordStatus?: string;
  questionId?: number;
};
export type RecordAttachmentConfirmationDto = {
  confirmations?: ConfirmationItemDto[];
  questionnaireConfirmations?: QuestionnaireAttachmentConfirmationDto[];
};
export type HierarchyFieldDtoTranslationUnitDto = {
  id?: number;
  hierarchyTypeId?: number;
  hierarchyTypeCaption?: TranslationUnitDto;
  parentId?: number;
  caption?: TranslationUnitDto;
  longitude?: number;
  latitude?: number;
  geofenceEnabled?: boolean;
  geofenceRadius?: number;
  archived?: boolean;
  lastChange?: string;
  deleted?: boolean;
  hierarchyFieldParentCaption?: TranslationUnitDto;
};
export type HierarchyDetailsDto = {
  caption?: string;
  latitude?: number;
  longitude?: number;
};
export type HierarchyFieldDtoHierarchyDetailsDto = {
  id?: number;
  hierarchyTypeId?: number;
  hierarchyTypeCaption?: HierarchyDetailsDto;
  parentId?: number;
  caption?: HierarchyDetailsDto;
  longitude?: number;
  latitude?: number;
  geofenceEnabled?: boolean;
  geofenceRadius?: number;
  archived?: boolean;
  lastChange?: string;
  deleted?: boolean;
  hierarchyFieldParentCaption?: HierarchyDetailsDto;
};
export type RecordFieldDtoObject = {
  id?: number;
  name?: string;
  fieldId?: number;
  type?:
    | "AI_FIELD"
    | "API_QUERY"
    | "CHECKBOX"
    | "CHECKBOX_CAPTION"
    | "CHECKBOX_DEFAULT"
    | "CHECKBOX_ICON"
    | "CLONERECORD"
    | "COMBOBOX"
    | "DATEFIELD"
    | "DOUBLEFIELD"
    | "EMAIL_THIS_BUTTON"
    | "GEOLOCATION"
    | "GRAND_SCORE"
    | "HISTORY"
    | "INTFIELD"
    | "INVITE_BUTTON"
    | "LABEL"
    | "LINK"
    | "MULTISELECTCHECKBOX"
    | "MULTISELECTFIELD"
    | "MULTISELECTPERSONFIELD"
    | "NUMERIC_QUESTIONNAIRE"
    | "OPTION_BUILDER"
    | "OPTIONGROUP"
    | "PDF_EXPORT"
    | "PERSONFIELD"
    | "READERS"
    | "RECORDLINK"
    | "REVERSE_RECORD_LINK"
    | "RICHTEXTAREA"
    | "RISKRATING"
    | "SIGNATURECAPTURE"
    | "SUMMARY_RECORD_LINK"
    | "TEXTAREA"
    | "TEXTFIELD"
    | "TEXTUAL_QUESTIONNAIRE"
    | "TIMEFIELD"
    | "TRAINING"
    | "TWINCOLUMNSELECT"
    | "TWINCOLUMNSELECTPERSONFIELD"
    | "USER_RECORDLINK"
    | "WORD_EXPORT";
  value?: object;
  result?: {
    [key: string]: object;
  };
};
export type RecordDto = {
  id?: number;
  sourceId?: string;
  moduleId?: number;
  moduleName?: string;
  viewId?: string;
  formId?: number;
  formName?: string;
  workflowStepId?: number;
  status?: string;
  docNo?: number;
  author?: UserDto;
  accountable?: UserDto[];
  creationDate?: string;
  lastModifiedDate?: string;
  lastChange?: string;
  dbLastChange?: string;
  linkUrl?: string;
  vikingLinkUrl?: string;
  deleted?: boolean;
  version?: number;
  displayText?: string;
  title?: string;
  calendarViewDate?: string;
  calendarViewCaption?: string;
  files?: string[];
  images?: string[];
  deletedFiles?: string[];
  mobile?: {
    [key: string]: string;
  };
  readAccessRoles?: string[];
  childRecordIds?: number[];
  workflowStepHistory?: {
    [key: string]: string;
  };
  attachmentInfo?: AttachmentDto[];
  sourceSystem?: "ONLINE_LEARNING" | "DOMINO";
  canViewRecordLinkRecord?: boolean;
  subfields?: {
    [key: string]: object;
  };
  attachmentConfirmation?: RecordAttachmentConfirmationDto;
  requiresApiSyncFields?: number[];
  requiresVisibilityCheckFields?: number[];
  requiresVisibilityCheckFieldGroups?: number[];
  requiresVisibilityCheckWorkflowButtons?: number[];
  mobileFields?: {
    [key: string]: object;
  };
  requireStepVisibilityCheck?: boolean;
  stepVisibilityRecordLinkConditionResult?: {
    [key: string]: boolean;
  };
  stepVisibilityRecordLinkConditionResults?: {
    [key: string]: {
      [key: string]: boolean;
    };
  };
  hierarchies?: HierarchyFieldDtoTranslationUnitDto[];
  hierarchyDetails?: HierarchyFieldDtoHierarchyDetailsDto[];
  fields?: RecordFieldDtoObject[];
};
export type EntityWithCaptionDtoTranslationUnitDto = {
  id?: number;
  caption?: TranslationUnitDto;
};
export type UserAssignmentDtoTranslationUnitDto = {
  targetFieldId?: number;
  userIds?: number[];
  groupIds?: number[];
  targetField?: EntityWithCaptionDtoTranslationUnitDto;
  users?: UserDto[];
  groups?: EntityWithCaptionDtoTranslationUnitDto[];
};
export type ScheduleRuleDtoTranslationUnitDtoBase = {
  id?: number;
  displayName?: TranslationUnitDto;
  type: string;
};
export type EntityWithCaptionDtoObject = {
  id?: number;
  caption?: object;
};
export type FieldConditionScheduleRuleDto = {
  type: "FieldConditionScheduleRuleDTO";
} & ScheduleRuleDtoTranslationUnitDtoBase & {
    displayName?: object;
    conditionFieldId?: number;
    fieldCaption?: object;
    conditionField?: EntityWithCaptionDtoObject;
    firstOccurrence?: number;
    repeatEvery?: number;
  };
export type PeriodicScheduleRuleDto = {
  type: "PeriodicScheduleRuleDTO";
} & ScheduleRuleDtoTranslationUnitDtoBase & {
    displayName?: object;
    scheduledRecordCreationType?: "DAYS" | "WEEKS" | "MONTHS" | "YEARS";
    hourOfDay?: number;
    dayOfMonth?: number;
    dayOfWeek?:
      | "MONDAY"
      | "TUESDAY"
      | "WEDNESDAY"
      | "THURSDAY"
      | "FRIDAY"
      | "SATURDAY"
      | "SUNDAY";
    month?:
      | "JANUARY"
      | "FEBRUARY"
      | "MARCH"
      | "APRIL"
      | "MAY"
      | "JUNE"
      | "JULY"
      | "AUGUST"
      | "SEPTEMBER"
      | "OCTOBER"
      | "NOVEMBER"
      | "DECEMBER";
    timeZone?: string;
    interval?: number;
  };
export type ScheduledRecordCreationSettingsDtoTranslationUnitDto = {
  initializationType?:
    | "TEMPLATE"
    | "BLANK"
    | "USER_FIELD"
    | "FORM_CONFIGURATION";
  hierarchySourcePersonField?: EntityWithCaptionDtoTranslationUnitDto;
  fieldsToApplyDefaultValues?: EntityWithCaptionDtoTranslationUnitDto[];
};
export type ScheduleDtoTranslationUnitDtoRecordDto = {
  id?: number;
  caption?: TranslationUnitDto;
  legacy?: boolean;
  copyTemplateRecordLinks?: boolean;
  template?: RecordDto;
  moduleForm?: EntityWithCaptionDtoTranslationUnitDto;
  targetWorkflowStep?: EntityWithCaptionDtoTranslationUnitDto;
  userAssignment?: UserAssignmentDtoTranslationUnitDto;
  rules?: (FieldConditionScheduleRuleDto | PeriodicScheduleRuleDto)[];
  hierarchyFields?: HierarchyFieldDtoTranslationUnitDto[];
  recordCreationSettings?: ScheduledRecordCreationSettingsDtoTranslationUnitDto;
  moduleFormCaption?: TranslationUnitDto;
  moduleFormId?: number;
  targetWorkflowStepCaption?: TranslationUnitDto;
  targetWorkflowStepId?: number;
};
export type RecordDetailsDtoTranslationUnitDto = {
  projected?: boolean;
  recordId?: number;
  docNumber?: number;
  status?: TranslationUnitDto;
  createdAt?: string;
  linkUrl?: string;
  hierarchyFields?: HierarchyFieldDtoTranslationUnitDto[];
};
export type I18NReport = {
  schedule?: ScheduleDtoTranslationUnitDtoRecordDto;
  title?: TranslationUnitDto;
  formCaption?: TranslationUnitDto;
  containsHistoricalRecords?: boolean;
  records?: RecordDetailsDtoTranslationUnitDto[];
};
export type EntityPageResponseDtoi18NReport = {
  validation?: Validation;
  /** the number of page */
  page?: number;
  /** the total number of pages associated with the original request */
  availablePages?: number;
  size?: number;
  totalSize?: number;
  items?: I18NReport[];
};
export type EntityResponseDtoMapStringListMapStringLong = {
  validation?: Validation;
  result?: {
    [key: string]: {
      [key: string]: number;
    }[];
  };
};
export type EntityPageResponseDtoHierarchyFieldDtoObject = {
  validation?: Validation;
  /** the number of page */
  page?: number;
  /** the total number of pages associated with the original request */
  availablePages?: number;
  size?: number;
  totalSize?: number;
  items?: HierarchyFieldDtoObject[];
};
export const {
  useGetThemeQuery,
  useLazyGetThemeQuery,
  useCreatedDynamicViewMutation,
  useGetAllSimpleModulesQuery,
  useLazyGetAllSimpleModulesQuery,
  useGetSimpleHierarchyTypes1Query,
  useLazyGetSimpleHierarchyTypes1Query,
  useGetEnvironmentQuery,
  useLazyGetEnvironmentQuery,
  useGetSchedulesReportsViewQuery,
  useLazyGetSchedulesReportsViewQuery,
  useDetailedCountForViewQuery,
  useLazyDetailedCountForViewQuery,
  useCountProjectedRecordsForViewQuery,
  useLazyCountProjectedRecordsForViewQuery,
  useGetSimpleHierarchyValuesQuery,
  useLazyGetSimpleHierarchyValuesQuery,
} = injectedRtkApi;
